import $http from '@vue-util/http';
import { ERROR_CODES } from '@/utils/constants';
import { brand, MY_ACCOUNT_FALL_BACK_ERROR } from 'config';
import { normalizeRewardsData } from '@/utils/loyalty';
import { fireErrorTag } from '@/utils/general';
import { useProfileStore } from './useProfileStore';
import { useProsStore } from './useProsStore';
const { globalErrorCodes, sessionOutErrorCodes } = ERROR_CODES;

const ORDER_HISTORY_ENDPOINT = '/api/ordermodsreturns/v1/post-purchase/myaccount/orders';

/* eslint-disable no-unused-vars*/
export const useDashboardStore = defineStore('dashboard', {
  state: () => ({
    killswitches: {},
    notification: {
      key: 0, // force vue to treat each notification as separate instance
      type: null,
      closeButton: false,
      component: null,
      message: null,
      globalError: false, // True will show global error notificaiton & hides account dashboard content.
      props: {},
    },
    offersList: {
      offers: null,
      notification: {
        type: '',
        message: '',
      },
    },
    offersError: false,
    rewardsSummary: null,
    orderHistory: {
      notification: {
        type: '',
        message: '',
      },
      orders: null,
    },
    ui: {
      modalOverlayStore: {
        id: '',
        props: {},
        component: null,
        title: '',
        width: '',
      },
    },
    signedInError: false,
    slideoutStack: [{ id: 'null' }],
    publicKey: '',
    keyVersion: 0,
    isWallet: false,
    starRewardsBanners: {
      STARREWARDS_BANNER_MY_ACCOUNT_DESKTOP: '',
      STARREWARDS_BANNER_MY_ACCOUNT_TABLET: '',
      STARREWARDS_BANNER_MY_ACCOUNT_MEW: '',
    },
  }),
  actions: {
    setIsWallet(value) {
      this.isWallet = value;
    },
    setAllOffers(offers) {
      this.offersList.offers = offers;
    },
    setOffersError(offersError) {
      this.offersError = offersError;
    },
    setNoOffersError(notification) {
      this.offersList.notification = notification;
    },
    setRewardsSummary(summary) {
      this.rewardsSummary = summary;
    },
    setStarRewardsBanners(banners) {
      this.starRewardsBanners = {
        ...this.starRewardsBanners,
        ...banners,
      };
    },
    setOrderHistory(orderHistory) {
      this.orderHistory.orders = orderHistory || [];
    },
    setOrderHistoryError(error) {
      this.orderHistory.notification = error;
    },
    setSlideout(slideoutDetail) {
      slideoutDetail.backable = this.slideoutStack.length > 1;
      this.slideoutStack.unshift(slideoutDetail);
    },
    resetSlideout() {
      this.slideoutStack = [{ id: 'null' }];
    },
    setModalOverlay(modalOverlayDetails) {
      this.ui.modalOverlayStore = modalOverlayDetails;
    },
    setGlobalNotification(notification) {
      const newKey = this.notification.key + 1;
      this.notification = notification;
      this.notification.key = newKey;
    },
    setSignedInError(signedInError) {
      this.signedInError = signedInError;
    },
    setEncryptionData(data) {
      this.publicKey = data.key;
      this.keyVersion = data.keyVersion;
    },
    setKillswitches(killswitches) {
      this.killswitches = killswitches;
    },

    async fetchUserSummary() {
      const profileStore = useProfileStore();
      const prosStore = useProsStore();
      try {
        const response = await $http.post(
          `/account-xapi/api/myaccount/user/summary?cardType=Offers,Profile${this.killswitches?.myAccountGetThumbnailEnabled ? '' : ',OrderHistory'}&timeStamp=${Date.now()}&sort=expirationdate`,
        );
        if (response.data.cards.error) {
          fireErrorTag(response.data.cards.error);
          if (sessionOutErrorCodes.indexOf(response.data.cards.error.errorCode) !== -1) {
            this.setSignedInError(true);
          }
          if (globalErrorCodes.indexOf(response.data.cards.error.errorCode) !== -1) {
            this.setGlobalNotification({
              type: 'error',
              closeButton: true,
              globalError: true,
              message: response.data.cards.error.message || MY_ACCOUNT_FALL_BACK_ERROR,
            });
          }
        } else {
          profileStore.setFirstName(response.data.cards.firstName);
          /* istanbul ignore if */
          if (response.data.cards.offers && response.data.cards.offers.length > 0) {
            if (response.data.cards.offers[0].error) {
              fireErrorTag(response.data.cards.offers[0].error);
              this.setNoOffersError({
                type: 'error',
                message: response.data.cards.offers[0].error.message || MY_ACCOUNT_FALL_BACK_ERROR,
              });
            } else {
              this.setOffersError(false);
              this.setAllOffers(response.data.cards.offers);
            }
          } else {
            this.setOffersError(true);
          }
          profileStore.setShippingAddress(response.data.cards.profile.shippingAddress);
          prosStore.setPros(response.data);
          if (!this.killswitches?.myAccountGetThumbnailEnabled) {
            this.setOrderHistory(response.data.cards.orderHistory.orders);
          }
        }
      } catch (error) {
        fireErrorTag(error);
        /* istanbul ignore if */
        if (error.data) {
          this.setOffersError(true);
          if (!this.killswitches?.myAccountGetThumbnailEnabled) {
            const errorMessage = error.data?.cards?.error?.message;
            this.setOrderHistoryError({
              type: 'error',
              message: errorMessage || "We're sorry, but we cannot display your information at this time.",
            });
          }
        } else {
          // handles network error or api response fails.
          this.setGlobalNotification({
            type: 'error',
            closeButton: true,
            globalError: true,
            message: "We're sorry, but we cannot display your information at this time.",
          });
        }
      }
    },
    async fetchRewardsSummary() {
      const url =
        brand === 'mcom'
          ? `/xapi/loyalty/v1/starrewardssummary?_pageType=myAccount&timeStamp=${Date.now()}`
          : '/xapi/loyalty/v1/accountsummary?_pageType=myAccount';
      try {
        const response = await $http.get(url);
        if (this.killswitches?.newLoyaltyAndCreditBannerEnabled) {
          this.setRewardsSummary(normalizeRewardsData(response.data));
        } else {
          this.setRewardsSummary(response.data);
        }
        //Check for globalPools
        const globalPools = response.data?.globalPools;
        if (Array.isArray(globalPools)) {
          const banners = {};
          globalPools.forEach((pool) => {
            if (
              ['STARREWARDS_BANNER_MY_ACCOUNT_DESKTOP', 'STARREWARDS_BANNER_MY_ACCOUNT_TABLET', 'STARREWARDS_BANNER_MY_ACCOUNT_MEW'].includes(
                pool.poolName,
              )
            ) {
              const fileName = pool.mediaList?.[0]?.fileName;
              if (fileName) {
                banners[pool.poolName] = fileName;
              }
            }
          });
          if (Object.keys(banners).length > 0) {
            this.setStarRewardsBanners(banners);
          }
        }
      } catch (error) {
        this.setRewardsSummary({ errors: { error: [{ message: MY_ACCOUNT_FALL_BACK_ERROR }] } });
      }
    },
    async fetchOrderHistoryThumbnails({ userId }) {
      const headers = {
        headers: {
          'X-Macys-UID': userId,
        },
      };
      const orderHistoryUrl = ORDER_HISTORY_ENDPOINT;

      $http
        .get(`${orderHistoryUrl}?timeStamp=${Date.now()}`, headers)
        .then((response) => this.setOrderHistory(response.data.orders))
        .catch((error) => {
          fireErrorTag(error);
          const errorMessage = error.response?.data?.errors?.error[0]?.message;
          this.setOrderHistoryError({ type: 'error', message: errorMessage || "We're sorry, but we cannot display your information at this time." });
        });
    },
    fetchEncryptionData() {
      $http
        .get('/account-xapi/api/encryption/getPublicKey')
        .then((response) => {
          if (response.data?.GetRSAKeyResponse) {
            this.setEncryptionData(response.data.GetRSAKeyResponse);
          }
        })
        .catch(() => {});
    },
  },
});
